import { create } from "zustand"

/**
 * Commission filter types for Affiliate pages
 */
export type AffiliateCommission = 
  | "BELOW_3"
  | "B3_5" 
  | "B5_10"
  | "B10_15"
  | "OVER_15"
  | string
  | null

/**
 * Filter state for Affiliate pages
 * Used for filtering products by commission and estimated earnings
 * Consolidated from useFilterStore and useFilterAffiliate
 */
export interface AffiliateFilterState {
  order?: "ASC" | "DESC"
  sortBy?: string
  status?: string
  startPrice?: number
  endPrice?: number
  startCommissionRate?: number
  endCommissionRate?: number
  effectiveStartTime?: string
  effectiveEndTime?: string
  // Legacy fields for backward compatibility
  commission?: AffiliateCommission
  est?: {
    select: "all" | "rang" | "over"
    rang: {
      startPrice: number
      endPrice: number
    }
    all: boolean
    over: boolean
  }
}

interface AffiliateFilterStore {
  // State
  countFilter: number
  showAllCategory: boolean
  filterState: AffiliateFilterState
  filter: AffiliateFilterState | null  // Alias for backward compatibility
  
  // Actions
  setCountFilter: (count: number) => void
  setShowAllCategory: (show: boolean) => void
  toggleShowAllCategory: () => void
  updateFilterState: (newState: Partial<AffiliateFilterState>) => void
  setFilter: (filter: AffiliateFilterState) => void  // For backward compatibility
  resetFilter: () => void
  reset: () => void  // Alias for resetFilter
  calculateFilterCount: () => number
  recalculateFilterCount: () => void
}

const DEFAULT_FILTER_STATE: AffiliateFilterState = {
  commission: null,
  sortBy: undefined,
  order: undefined,
  est: {
    select: "all",
    rang: {
      startPrice: 5000,
      endPrice: 500000
    },
    all: false,
    over: false
  }
}

const createDefaultFilter = (): AffiliateFilterState => ({
  ...DEFAULT_FILTER_STATE
})

// Helper function to convert commission range to rate values
const getCommissionRates = (commission: AffiliateCommission): { start?: number; end?: number } => {
  switch (commission) {
    case "BELOW_3":
      return { start: 0, end: 3 }
    case "B3_5":
      return { start: 3, end: 5 }
    case "B5_10":
      return { start: 5, end: 10 }
    case "B10_15":
      return { start: 10, end: 15 }
    case "OVER_15":
      return { start: 15, end: 100 }
    default:
      return {}
  }
}

// Transform form data to API query params
export const transformFilterToQueryParams = (formData: any): Partial<AffiliateFilterState> => {
  const params: Partial<AffiliateFilterState> = {}
  
  // Handle sortBy and order - convert form value to API value
  if (formData.sortBy) {
    if (formData.sortBy === "NEWEST") {
      params.sortBy = "createdAt"
      params.order = "DESC"
    } else if (formData.sortBy === "OLDEST") {
      params.sortBy = "createdAt"
      params.order = "ASC"
    }
  }
  
  // Handle price range
  if (formData.est) {
    if (formData.est.select === "rang" && formData.est.rang) {
      params.startPrice = formData.est.rang.startPrice
      params.endPrice = formData.est.rang.endPrice
    } else if (formData.est.select === "over") {
      params.startPrice = 500000
    }
  }
  
  // Handle commission rate
  if (formData.commission) {
    const rates = getCommissionRates(formData.commission)
    if (rates.start !== undefined) params.startCommissionRate = rates.start
    if (rates.end !== undefined) params.endCommissionRate = rates.end
  }
  
  return params
}

const calculateFilterCount = (state: AffiliateFilterState): number => {
  let count = 0
  
  // Count est filter only if it's not default "all"
  if (state.est && state.est.select !== "all") count++
  
  // Count commission filter
  if (state.commission && state.commission !== null) count++
  
  // Count sort filter only if it's set and not default
  if (state.sortBy || (state.order && state.order !== "DESC")) count++
  
  // Don't double count these as they're transformed from est and commission
  // Only count if they're set directly without est/commission
  if (!state.est || state.est.select === "all") {
  if (state.startPrice !== undefined || state.endPrice !== undefined) count++
  }
  
  if (!state.commission) {
  if (state.startCommissionRate !== undefined || state.endCommissionRate !== undefined) count++
  }
  
  // Count date range filter
  if (state.effectiveStartTime || state.effectiveEndTime) count++
  
  return count
}

export const useFilterStore = create<AffiliateFilterStore>()((set, get) => ({
  // Initial state
  countFilter: 0,
  showAllCategory: false,
  filterState: createDefaultFilter(),
  get filter() {
    return get().filterState
  },

  // Actions
  setCountFilter: (count) => set({ countFilter: count }),
  
  setShowAllCategory: (show) => set({ showAllCategory: show }),
  
  toggleShowAllCategory: () => 
    set((state) => ({ showAllCategory: !state.showAllCategory })),
  
  updateFilterState: (newState) => {
    set((state) => {
      const updatedState = { ...state.filterState, ...newState }
      const count = calculateFilterCount(updatedState)
      return {
        filterState: updatedState,
        countFilter: count
      }
    })
  },

  setFilter: (filter) => {
    const count = calculateFilterCount(filter)
    set({
      filterState: { ...filter },
      countFilter: count
    })
  },
  
  resetFilter: () => {
    set({
      filterState: createDefaultFilter(),
      countFilter: 0
    })
  },

  reset: () => {
    set({
      filterState: createDefaultFilter(),
      countFilter: 0
    })
  },
  
  calculateFilterCount: () => {
    const state = get()
    return calculateFilterCount(state.filterState)
  },
  
  recalculateFilterCount: () => {
    const state = get()
    const count = calculateFilterCount(state.filterState)
    set({ countFilter: count })
  }
}))
